import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { CalendarBlank, Headset } from "@phosphor-icons/react";

const NONE = "__none__";
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dayLabel = (d) => { const x = new Date(`${d}T12:00:00`); return `${DAYS[x.getDay()]} ${x.getDate()} ${MONTHS[x.getMonth()]}`; };

function RotaRow({ r, team, canManage, tonight, onSet }) {
  const tag = r.bank_holiday ? "Bank holiday" : r.weekend ? "Weekend" : "";
  return (
    <li className={`grid grid-cols-[1fr_auto] sm:grid-cols-[150px_1fr_auto] gap-2 items-center rounded-md border px-3 py-1.5 ${r.date === tonight ? "border-indigo-400 bg-indigo-50/60 dark:bg-indigo-950/30" : "border-border"}`} data-testid={`ooh-rota-row-${r.date}`}>
      <div className="text-sm">
        <p className="font-medium">{dayLabel(r.date)}{r.date === tonight && <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-600 text-white">tonight</span>}</p>
        <p className="text-[10px] text-muted-foreground">{tag ? `${tag} — all day` : "17:00 → 08:00"}</p>
      </div>
      <div className="min-w-0 col-span-2 sm:col-span-1">
        {canManage ? (
          <Select value={r.user_id || NONE} onValueChange={(v) => onSet(r.date, v === NONE ? "" : v)}>
            <SelectTrigger className="h-8 text-xs" data-testid={`ooh-rota-select-${r.date}`}><SelectValue placeholder="Nobody on call" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Nobody on call</SelectItem>
              {team.map((u) => <SelectItem key={u._id} value={u._id} data-testid={`ooh-rota-option-${r.date}-${u._id}`}>{u.name}{u.operations_director ? " · Ops director" : u.ooh_admin ? " · specialist" : " · covering"}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <p className="text-sm" data-testid={`ooh-rota-name-${r.date}`}>{r.user_name || <span className="text-muted-foreground">Nobody on call</span>}</p>
        )}
      </div>
      <p className="text-xs font-mono text-muted-foreground hidden sm:block" data-testid={`ooh-rota-phone-${r.date}`}>{r.user_id ? r.phone || "no phone on file" : ""}</p>
    </li>
  );
}

export default function OohRotaPanel({ onChanged }) {
  const [data, setData] = useState(null);
  const [start, setStart] = useState("");
  const load = useCallback(async () => {
    try { const { data: d } = await API.get("/ooh/rota", { params: { start, days: 14 } }); setData(d); }
    catch { toast.error("Couldn't load the rota"); }
  }, [start]);
  useEffect(() => { load(); }, [load]);
  const set = async (night, userId) => {
    try {
      const { data: e } = await API.put(`/ooh/rota/${night}`, { user_id: userId });
      toast.success(userId ? `${e.user_name} is on call ${dayLabel(night)}` : `Nobody on call ${dayLabel(night)}`);
      load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update the rota"); }
  };
  if (!data) return <p className="text-xs text-muted-foreground">Loading rota…</p>;
  const shift = (n) => { const d = new Date(`${data.start}T12:00:00`); d.setDate(d.getDate() + n); setStart(d.toISOString().slice(0, 10)); };
  return (
    <div className="space-y-3" data-testid="ooh-rota-panel">
      <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm flex items-center gap-2" data-testid="ooh-rota-tonight">
        <Headset size={16} weight="fill" className={data.on_call ? "text-emerald-600" : "text-amber-500"} />
        {data.on_call ? <>Tonight: <strong>{data.on_call.name}</strong>{data.on_call.phone ? <span className="font-mono text-xs">· {data.on_call.phone}</span> : null} — alerts and the call-out emails go to them only.</> : <>Nobody rostered tonight — alerts go to every OOH team member.</>}
      </div>
      <p className="text-xs text-muted-foreground">Each row is one night (17:00 → 08:00 next day; weekends and bank holidays are all day). Only OOH team members can be rostered.{!data.can_manage && " Only an Operations Director or super admin can change the rota."}</p>
      <div className="flex items-center justify-between">
        <Button size="sm" variant="ghost" onClick={() => shift(-14)} data-testid="ooh-rota-prev">← Earlier</Button>
        <span className="text-xs font-medium inline-flex items-center gap-1"><CalendarBlank size={13} /> {dayLabel(data.rows[0].date)} – {dayLabel(data.rows[data.rows.length - 1].date)}</span>
        <Button size="sm" variant="ghost" onClick={() => shift(14)} data-testid="ooh-rota-next">Later →</Button>
      </div>
      <ul className="space-y-1.5" data-testid="ooh-rota-list">{data.rows.map((r) => <RotaRow key={r.date} r={r} team={data.team} canManage={data.can_manage} tonight={data.tonight} onSet={set} />)}</ul>
    </div>
  );
}
